import { jsPDF } from 'jspdf';
import { CompanyDetails } from '../pages/LeiPage/CompanyDetails';
import countries from 'i18n-iso-countries';
import { fetchManagingLou } from './api';

// Register fonts
const registerFonts = (doc: jsPDF) => {
  // Add RobotoMono fonts
  doc.addFont('/fonts/RobotoMono-Regular.ttf', 'RobotoMono', 'normal');
  doc.addFont('/fonts/RobotoMono-Bold.ttf', 'RobotoMono', 'bold');
};

countries.registerLocale(require("i18n-iso-countries/langs/en.json"));

export const generateLeiCertificate = async (companyDetails: CompanyDetails): Promise<void> => {
  // Fetch Managing LOU name
  let managingLouName = companyDetails.registration.managingLou;
  try {
    const entityData = await fetchManagingLou(companyDetails.registration.managingLou);
    managingLouName = entityData.data.attributes.entity.legalName.name;
  } catch (error) {
    console.error('Error fetching managing LOU legal entity:', error);
  }

  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: true,
    precision: 3
  });

  // Register custom fonts
  registerFonts(doc);
  
  // Set default font to RobotoMono
  doc.setFont('RobotoMono', 'normal');

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const flagWidth = 20; // 4 colors * 5px each
  const flagMarginRight = 15
  const contentWidth = pageWidth - (2 * margin) - flagWidth - 10; // Extra space for flag

  // Add background pattern
  try {
    const patternImg = new Image();
    patternImg.src = '/pattern.png';
    await new Promise((resolve, reject) => {
      patternImg.onload = resolve;
      patternImg.onerror = reject;
    });
    
    // Pattern dimensions: 2000x1500 pixels
    // Scale down the pattern to be smaller (adjust scale factor as needed)
    const scaleFactor = 0.9; // Adjust this to make pattern smaller or larger
    const patternWidthMm = pageWidth * scaleFactor;
    const patternHeightMm = (1500 / 2000) * patternWidthMm; // Maintain aspect ratio
    
    // Calculate how many times to tile the pattern
    const tilesX = Math.ceil(pageWidth / patternWidthMm);
    const tilesY = Math.ceil(pageHeight / patternHeightMm);
    
    // Tile the pattern to cover the entire page
    for (let x = 0; x < tilesX; x++) {
      for (let y = 0; y < tilesY; y++) {
        const xPos = x * patternWidthMm;
        const yPos = y * patternHeightMm;
        doc.addImage(patternImg, 'PNG', xPos, yPos, patternWidthMm, patternHeightMm);
      }
    }
  } catch (error) {
    console.error('Error loading background pattern:', error);
  }

  // Add vertical flag on the right side (full page height)
  const flagX = pageWidth - flagMarginRight - flagWidth;
  const flagColors = ['#e8464e', '#2b3956', '#4b7196', '#cfe2f3'];
  const colorWidth = 6;
  
  flagColors.forEach((color, index) => {
    // Convert hex to RGB
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    
    doc.setFillColor(r, g, b);
    doc.rect(flagX + (index * colorWidth), 0, colorWidth, pageHeight, 'F');
  });

  // Title
  doc.setFontSize(36);
  doc.setTextColor(0,0,0);
  doc.setFont('helvetica', 'bold');
  const titleX = (pageWidth - flagWidth - flagMarginRight) / 2;
  doc.text('CERTIFICATE', titleX, 40, { align: 'center' });

  // Subtitle
  doc.setFontSize(18);
  doc.setTextColor(0,0,0);
  doc.setFont('helvetica', 'normal');
  doc.text('LEGAL ENTITY IDENTIFIER', titleX, 50, { align: 'center' });

  // This certifies
  doc.setFontSize(14);
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'italic');
  doc.text('This certifies that', titleX, 68, { align: 'center' });

let yPosition = 80;

  // Legal Name in RobotoMono Bold
  doc.setFontSize(20);
  doc.setFont('RobotoMono', 'bold');
  doc.setTextColor(0, 0, 0);
  const legalNameText = doc.splitTextToSize(companyDetails.legalName.name, contentWidth - 40);
  doc.text(legalNameText, titleX, yPosition, { align: 'center' });
  yPosition += legalNameText.length * 7 + 8;

  // Certificate text
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(14);
  const certText = 'has the following Legal Entity Identifier (LEI) registration details recorded in the Global LEI Index.';
  const splitCertText = doc.splitTextToSize(certText, contentWidth);
  doc.text(splitCertText, titleX, yPosition, { align: 'center', maxWidth: contentWidth });
  yPosition += splitCertText.length * 6 + 12;

  // Company Information Section
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(75, 113, 150); // #4b7196
  doc.text('ENTITY INFORMATION', margin, yPosition);
  yPosition += 8;

  const countryName = countries.getName(companyDetails.jurisdiction, "en") || companyDetails.jurisdiction;
  const labelWidth = 45;
  
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);

  // Jurisdiction
  doc.setFont('helvetica', 'bold');
  doc.text('Jurisdiction:', margin, yPosition);
  doc.setFont('RobotoMono', 'bold');
  const jurisdictionText = doc.splitTextToSize(`${countryName} (${companyDetails.jurisdiction})`, contentWidth - labelWidth);
  doc.text(jurisdictionText, margin + labelWidth, yPosition);
  yPosition += 6;

  // Registration Number
  doc.setFont('helvetica', 'bold');
  doc.text('Registration No.:', margin, yPosition);
  doc.setFont('RobotoMono', 'bold');
  doc.text(companyDetails.registeredAs || 'N/A', margin + labelWidth, yPosition);
  yPosition += 6;

  // LEI Code
  doc.setFont('helvetica', 'bold');
  doc.text('LEI:', margin, yPosition);
  doc.setFont('RobotoMono', 'bold');
  doc.text(companyDetails.lei, margin + labelWidth, yPosition);
  yPosition += 6;

  // Entity Status
  doc.setFont('helvetica', 'bold');
  doc.text('Entity status:', margin, yPosition);
  doc.setFont('RobotoMono', 'bold');
  doc.text(companyDetails.status, margin + labelWidth, yPosition);
  yPosition += 6;

  // LEI Status
  doc.setFont('helvetica', 'bold');
  doc.text('LEI status:', margin, yPosition);
  doc.setFont('RobotoMono', 'bold');
  doc.text(companyDetails.registration.status, margin + labelWidth, yPosition);
  yPosition += 6;

  // Certificate Date
  doc.setFont('helvetica', 'bold');
  doc.text('Certificate date:', margin, yPosition);
  doc.setFont('RobotoMono', 'bold');
  doc.text(new Date().toLocaleDateString(), margin + labelWidth, yPosition);
  yPosition += 10;

  // Registration Details Section
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(75, 113, 150); // #4b7196
  doc.text('REGISTRATION DETAILS', margin, yPosition);
  yPosition += 8;

  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);

  // Initial Registration
  doc.setFont('helvetica', 'bold');
  doc.text('Initial registration:', margin, yPosition);
  doc.setFont('RobotoMono', 'bold');
  doc.text(new Date(companyDetails.registration.initialRegistrationDate).toLocaleDateString(), margin + labelWidth, yPosition);
  yPosition += 6;

  // Last Update
  doc.setFont('helvetica', 'bold');
  doc.text('Last update:', margin, yPosition);
  doc.setFont('RobotoMono', 'bold');
  doc.text(new Date(companyDetails.registration.lastUpdateDate).toLocaleDateString(), margin + labelWidth, yPosition);
  yPosition += 6;

  // Next LEI Renewal
  doc.setFont('helvetica', 'bold');
  doc.text('Next LEI renewal:', margin, yPosition);
  doc.setFont('RobotoMono', 'bold');
  doc.text(new Date(companyDetails.registration.nextRenewalDate).toLocaleDateString(), margin + labelWidth, yPosition);
  yPosition += 6;

  // LEI Issuer (Managing LOU)
  doc.setFont('helvetica', 'bold');
  doc.text('LEI issuer:', margin, yPosition);
  doc.setFont('RobotoMono', 'bold');
  const nbsp = '\u00A0';
  const leiIssuerText = doc.splitTextToSize(`${managingLouName} LEI${nbsp}${companyDetails.registration.managingLou}`, contentWidth - labelWidth);
  doc.text(leiIssuerText, margin + labelWidth, yPosition);
  yPosition += leiIssuerText.length * 5 + 8;

  // Add logo after LEI issuer, aligned to left
  try {
    const logoImg = new Image();
    logoImg.src = '/leipapa-logo.png';
    await new Promise((resolve, reject) => {
      logoImg.onload = resolve;
      logoImg.onerror = reject;
    });
    // Rectangle logo - adjust width and height as needed
    const logoWidth = 40;
    const logoHeight = 14;
    doc.addImage(logoImg, 'PNG', margin, yPosition, logoWidth, logoHeight);
    yPosition += logoHeight + 5;
  } catch (error) {
    console.error('Error loading logo:', error);
  }

  // Footer
  const footerY = pageHeight - 45;
  doc.setFontSize(8);
  doc.setTextColor(0,0,0);
  doc.setFont('helvetica', 'normal');
  const footerText = 'This certificate is generated from LEIprofile.com based on the data from the Global LEI Index. LEIprofile.com belongs to LEIpapa OÜ (LEIpapa), which is an official Registration Agent of RapidLEI Ubisecure. LEIpapa is not responsible for the accuracy of the data on the LEI record. This certificate does not necessarily indicate that the LEI was initially registered via LEIpapa, as the entity owner can transfer the LEI to any other service provider. This certifies that the above entity is a confirmed legal entity with an LEI record and the specified status on the GLEIF database. This data belongs to GLEIF.';
  const splitFooterText = doc.splitTextToSize(footerText, contentWidth);
  doc.text(splitFooterText, margin, footerY, { align: 'justify', maxWidth: contentWidth });
  
  const footerTextHeight = splitFooterText.length * 3;
  let footerLineY = footerY + footerTextHeight + 5;
  
  // Copyright section
  doc.setFont('helvetica', 'normal');
  const currentYear = new Date().getFullYear();
  const dateText = `Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`;
  const centerX = (pageWidth - flagWidth - flagMarginRight) / 2;
  doc.text('https://leiprofile.com; https://leipapa.com', centerX, footerLineY, { align: 'center' });
  footerLineY += 3.5;
  doc.text(dateText, centerX, footerLineY, { align: 'center' });
  footerLineY += 3.5;
  
  doc.text(`© 2021 - ${currentYear} LEIpapa OÜ. LEIpapa is a registered TM®. Made in Estonia.`, centerX, footerLineY, { align: 'center' });
  footerLineY += 3.5;
  
  
  // Save the PDF
  const fileName = `LEI_Certificate_${companyDetails.lei}_${companyDetails.legalName.name.replace(/[^a-z0-9]/gi, '_')}.pdf`;
  doc.save(fileName);
};
